import { Dialog as BaseDialog } from '@base-ui-components/react/dialog';
import { cn } from '../cn.js';
import { IconClose } from '../icons.js';
import { IconButton } from './icon-button.js';

export type SheetSide = 'left' | 'right' | 'bottom';

export interface SheetContentProps extends BaseDialog.Popup.Props {
  readonly side?: SheetSide;
  /**
   * How wide a side sheet is. `panel` for navigation and short details; `form` for an editor whose
   * fields sit two or three to a row, which a panel would stack into a column twice as long.
   */
  readonly size?: 'panel' | 'form';
  /** The sheet's accessible name. A dialog without one is a region nobody can identify. */
  readonly title: string;
  /** A sentence under the title, read out with it when the sheet opens. */
  readonly description?: string | undefined;
  /** Hides the title visually while keeping it for assistive technology. */
  readonly titleHidden?: boolean;
  readonly closeLabel?: string;
}

/**
 * A panel that comes in from an edge.
 *
 * Base UI's dialog, positioned. It is the same machinery — focus trap, escape, scroll lock,
 * restoring focus to the trigger — so a sheet is a dialog that arrives from the side rather than a
 * second thing to keep in step with one. `vaul`'s drawer would add a drag-to-dismiss gesture, and
 * this product targets a desktop first with a tablet second, so it would be a dependency for a
 * gesture nobody makes.
 *
 * **It slides, where a dialog fades, and that is not a contradiction.** A dialog scaling makes its
 * contents start narrower than they end and resamples every glyph; a sheet *translates*, which
 * moves the pixels without redrawing them. Arriving from an edge is also what tells a person where
 * it came from and where it will go back to — the one piece of information the motion carries.
 */
const bySide: Record<SheetSide, { position: string; enter: string; size: string }> = {
  left: {
    position: 'inset-y-0 left-0 border-r',
    enter: 'data-[starting-style]:-translate-x-full data-[ending-style]:-translate-x-full',
    size: 'h-full w-(--width-panel) max-w-full',
  },
  right: {
    position: 'inset-y-0 right-0 border-l',
    enter: 'data-[starting-style]:translate-x-full data-[ending-style]:translate-x-full',
    size: 'h-full w-(--width-panel) max-w-full',
  },
  bottom: {
    position: 'inset-x-0 bottom-0 border-t',
    enter: 'data-[starting-style]:translate-y-full data-[ending-style]:translate-y-full',
    /*
     * A fraction of the viewport is not a theme value: a tenant cannot meaningfully rebrand "most
     * of the screen", and a token for it would make the token set describe this component rather
     * than the design.
     *
     * `dvh` and not `vh`, because on a phone `vh` is the viewport with the address bar hidden —
     * the sheet's bottom edge would sit underneath it until the bar retracted.
     */
    // biome-ignore lint: a viewport fraction, explained above.
    size: 'max-h-[80dvh] w-full',
  },
};

export const Sheet = BaseDialog.Root;
export const SheetTrigger = BaseDialog.Trigger;
export const SheetClose = BaseDialog.Close;

export function SheetContent({
  className,
  side = 'left',
  size = 'panel',
  title,
  description,
  titleHidden = false,
  closeLabel = 'Close',
  children,
  ...props
}: SheetContentProps) {
  const style = bySide[side];
  return (
    <BaseDialog.Portal>
      <BaseDialog.Backdrop
        className={cn(
          'fixed inset-0 z-(--elevation-overlay) bg-(--color-overlay-scrim)',
          'transition-opacity duration-(--motion-duration-normal) ease-(--motion-easing-standard)',
          'data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
          'motion-reduce:transition-none',
        )}
      />
      <BaseDialog.Popup
        className={cn(
          'fixed z-(--elevation-dialog) flex flex-col overflow-y-auto',
          'border-(--color-border) bg-(--color-surface-elevated) text-(--color-text-primary)',
          'shadow-(--shadow-overlay)',
          // Translate, not scale: it moves the pixels rather than redrawing them.
          'transition-transform duration-(--motion-duration-normal) ease-(--motion-easing-emphasized)',
          'motion-reduce:transition-none',
          style.position,
          style.size,
          size === 'form' && side !== 'bottom' ? 'w-(--width-container-narrow)' : null,
          style.enter,
          className,
        )}
        {...props}
      >
        <div className="flex items-start justify-between gap-(--space-md) p-(--space-md)">
          <div className="flex min-w-0 flex-col gap-(--space-xs)">
            <BaseDialog.Title
              className={cn(
                titleHidden
                  ? 'sr-only'
                  : 'text-(length:--font-size-body-large) font-semibold text-(--color-text-primary)',
              )}
            >
              {title}
            </BaseDialog.Title>
            {description === undefined ? null : (
              <BaseDialog.Description className="text-(length:--font-size-small) text-(--color-text-secondary)">
                {description}
              </BaseDialog.Description>
            )}
          </div>
          <BaseDialog.Close render={<IconButton icon={IconClose} label={closeLabel} size="sm" />} />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-(--space-md) pt-0">{children}</div>
      </BaseDialog.Popup>
    </BaseDialog.Portal>
  );
}
